import { observer } from "mobx-react-lite";
import { FC } from "react";

import {
  Badge,
  dataAttribute,
  DataAttributes,
  FontWeights,
  IColumn,
  RESET_CELLS_PADDING_CLASSNAME,
  ScrollablePane,
  Stack,
  Text
} from "@bps/fluent-ui";
import {
  MedicalCertainty,
  MedicalHistoryClinicalDataItemDto,
  MedicalHistoryTestElements
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { usePatientRecordScreenContext } from "@modules/clinical/screens/context/PatientRecordScreenContext.ts";
import { TerminologyText } from "@modules/clinical/screens/patient-record/components/TerminologyText.tsx";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { User } from "@stores/core/models/User.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { MedicalCertaintiesText } from "@ui-components/RefText.tsx";
import { ShimmeredDetailsList } from "@ui-components/ShimmeredDetailsList/ShimmeredDetailsList.tsx";

import { ConfidentialToolTipFontIcon } from "../ConfidentialToolTipFontIcon.tsx";
import { MedicalHistoryFilterType } from "./MedicalHistoryFilter.types.ts";
import { MedicalHistoryListItemMenu } from "./MedicalHistoryListItemMenu.tsx";
import {
  filterData,
  formatDiagnosisDate,
  sortMedicalHistoryClinicalDates
} from "./utils.ts";

interface MedicalHistoryListProps {
  clinicalRecord: ClinicalRecord;
  filter: MedicalHistoryFilterType;
  onDeleteDiagnosis: (item: MedicalHistoryClinicalDataItemDto) => void;
  openMedicalHistoryDialog: (value: MedicalHistoryClinicalDataItemDto) => void;
  users: User[];
}

export const MedicalHistoryList: FC<MedicalHistoryListProps> = observer(
  ({
    clinicalRecord,
    filter,
    onDeleteDiagnosis,
    openMedicalHistoryDialog,
    users
  }) => {
    const { clinical, core } = useStores();

    let medicalHistories =
      clinicalRecord.clinicalData?.medicalHistory?.medicalHistories ?? [];

    //Confidential feature toggle
    if (!core.hasPermissions(Permission.ConfidentialDataAllowed)) {
      medicalHistories = medicalHistories.filter(x => !x.secGroupId);
    }

    const getFilteredItems = (
      dataSource: MedicalHistoryClinicalDataItemDto[],
      filter: MedicalHistoryFilterType
    ): MedicalHistoryClinicalDataItemDto[] =>
      sortMedicalHistoryClinicalDates(
        dataSource.filter(i =>
          filterData(i, filter, {
            terminology: i.diagnosis
              ? clinical.terminologyMap.get(i.diagnosis)
              : undefined,
            hasAccessToSecGroup: core.hasAccessToSecGroup(i.secGroupId)
          })
        )
      );

    const { isViewOnlyOrDischarged } = usePatientRecordScreenContext();

    const columns: IColumn[] = [];

    if (!isViewOnlyOrDischarged) {
      columns.push({
        key: "button",
        onRender: (item: MedicalHistoryClinicalDataItemDto) => {
          return (
            <MedicalHistoryListItemMenu
              item={item}
              clinicalRecord={clinicalRecord}
              onDeleteDiagnosis={onDeleteDiagnosis}
              openMedicalHistoryDialog={openMedicalHistoryDialog}
            />
          );
        },
        name: "",
        className: RESET_CELLS_PADDING_CLASSNAME,
        minWidth: 30,
        maxWidth: 30
      });
    }

    columns.push({
      key: "date",
      name: "Date",
      minWidth: 100,
      maxWidth: 100,
      isMultiline: true,
      onRender: item => {
        return (
          <Stack>
            <Text
              styles={{ root: { paddingBottom: 4 } }}
              {...dataAttribute(
                DataAttributes.Element,
                MedicalHistoryTestElements.DateColumn
              )}
            >
              {formatDiagnosisDate(item.diagnosisDate)}
            </Text>
            {item.certainty &&
              item.certainty !== MedicalCertainty.Confirmed && (
                <Badge
                  styles={{
                    root: { borderRadius: 2 }
                  }}
                  {...dataAttribute(
                    DataAttributes.Element,
                    MedicalHistoryTestElements.CertaintyColumn
                  )}
                >
                  <MedicalCertaintiesText code={item.certainty} />
                </Badge>
              )}
          </Stack>
        );
      }
    });

    const getSideText = (item: { diagnosisSide: any }) => {
      if (item.diagnosisSide) {
        const sideOfBody = clinical.ref.sidesOfBody.keyTextValues.find(
          x => x.key === item.diagnosisSide
        )?.text;

        if (sideOfBody) {
          return `(${sideOfBody})`;
        }
      }

      return "";
    };

    columns.push({
      key: "details",
      name: "Diagnosis / Procedure",
      minWidth: 150,
      maxWidth: 150,
      isMultiline: true,
      onRender: item => {
        const providerName = users.find(
          x => x.privateSecGroupId === item.secGroupId
        )?.fullName;

        return core.hasAccessToSecGroup(item.secGroupId) ? (
          <Stack
            horizontal
            wrap
            styles={{
              root: {
                paddingBottom: 4,
                display: "flex"
              }
            }}
          >
            <Text
              {...dataAttribute(
                DataAttributes.Element,
                MedicalHistoryTestElements.DiagnosisColumn
              )}
            >
              <TerminologyText
                codedTexts={[
                  {
                    code: item.diagnosisCode?.code,
                    text: `${item.diagnosisCode?.originalText} ${getSideText(
                      item
                    )}`
                  }
                ]}
              />
            </Text>

            <ConfidentialToolTipFontIcon
              isShowConfidentialIcon={item.secGroupId}
              content="Confidential. Use action menu to release"
            />

            {!item.active && (
              <Text
                styles={{
                  root: { fontWeight: FontWeights.bold, paddingLeft: "4px" }
                }}
              >
                (inactive)
              </Text>
            )}
          </Stack>
        ) : (
          <Stack horizontal>
            <Text>{`Confidential by ${providerName}`}</Text>
          </Stack>
        );
      }
    });

    return (
      <div style={{ position: "relative", flexGrow: 1 }}>
        <ScrollablePane styles={{ root: { height: "100%", marginTop: 8 } }}>
          <ShimmeredDetailsList
            setKey="medicalHistoryDetailList"
            columns={columns.map(c => ({
              ...c,
              onRender: (item, index) => (
                <div {...dataAttribute(DataAttributes.Element, c.key)}>
                  {c.onRender ? c.onRender(item, index) : c.name}
                </div>
              )
            }))}
            items={getFilteredItems(medicalHistories, filter)}
          />
        </ScrollablePane>
      </div>
    );
  }
);
